'use client';
import { forwardRef, Fragment, ReactNode } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { HiX } from 'react-icons/hi';

export interface ModalProps extends React.PropsWithChildren {
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
    bg?: 'white' | 'light' | 'dark' | 'contrast';
    title?: ReactNode;
    padded?: boolean;
    show: boolean;
    onClose: () => void;
}
const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
};

const bgClasses = {
    white: 'bg-white',
    light: 'bg-lightest-grey',
    dark: 'bg-dark-green',
    contrast: 'bg-contrast-grey',
};

const Modal = forwardRef<HTMLDivElement, ModalProps>(
    ({ size = 'sm', bg = 'white', padded = true, show, onClose, children, title }: ModalProps, ref) => {
        return (
            <Transition appear show={show} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div aria-hidden={true} className="bg-dark-green/75 fixed inset-0" />
                    </TransitionChild>

                    <div className="fixed inset-0">
                        <div className={`flex h-full items-center justify-center ${padded ? 'p-4' : ''}`}>
                            <TransitionChild
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95">
                                <DialogPanel
                                    className={`relative ${sizeClasses[size]} flex max-h-[80%] w-full flex-col justify-stretch ${bgClasses[bg]} transform text-left align-middle drop-shadow-lg transition-all`}>
                                    {title && (
                                        <DialogTitle as="div" className="relative flex flex-col pr-10">
                                            {title}
                                            <HiX
                                                onClick={onClose}
                                                role="button"
                                                className="hover:text-primary absolute right-4 top-4 cursor-pointer text-lg"
                                            />
                                        </DialogTitle>
                                    )}
                                    <div className="min-h-0 flex-1 overflow-auto">{children}</div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        );
    },
);

Modal.displayName = 'Modal';

export default Modal;
